import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { firestore } from '../../firebase';
import NavigationConfirmModal from '../../components/modals/NavigationConfirmModal';
import { notificationEvents } from '../../util/notificationEvents';

const BottomNavigation = ({ credentials }) => {
  const history = useHistory();
  const location = useLocation();
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Check for unread notifications
  const checkNotifications = async () => {
    const authenticatedUserHandle = credentials?.handle;
    
    if (!authenticatedUserHandle) return;
    try {
      const unreadQuery = query(
        collection(firestore, "notifications"),
        where("status", "==", "unread"),
        where("targetUser", "==", authenticatedUserHandle),
        limit(1)
      );
      
      const snapshot = await getDocs(unreadQuery);
      setHasUnreadNotifications(!snapshot.empty);
    } catch (error) {
      console.error("Error checking notifications:", error);
    }
  };

  // Clear notification status when user visits interaction page
  const clearNotificationStatus = () => {
    setHasUnreadNotifications(false);
  };

  // Check notifications on load and focus events
  useEffect(() => {
    if (credentials?.handle) {
      checkNotifications();
      
      const handleFocus = () => checkNotifications();
      const handleVisibilityChange = () => {
        if (!document.hidden) checkNotifications();
      };
      
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Subscribe to notification status changes from other components
      const unsubscribe = notificationEvents.subscribe((hasUnreadNotifications) => {
        setHasUnreadNotifications(hasUnreadNotifications);
      });
      
      return () => {
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        unsubscribe();
      };
    }
  }, [credentials?.handle]);

  // Clear notifications when visiting interaction page
  useEffect(() => {
    if (location.pathname === '/interaction') {
      clearNotificationStatus();
    }
  }, [location.pathname]);

  // Render placeholder if credentials are not available to prevent layout shifts
  if (!credentials) {
    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '70px',
        backgroundColor: '#1a1a1a',
        borderTop: '1px solid #333',
        zIndex: 9999,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }} />
    );
  }
  
  const navItems = [
    {
      path: '/portal',
      icon: 'fa-solid fa-home',
    },
    {
      path: '/interaction',
      icon: 'fa-solid fa-bell',
    },
    {
      path: '/Analysis',
      icon: 'fa-solid fa-square-plus',
    },
    {
      path: '/profile',
      icon: 'fa-solid fa-user',
    },
    {
      path: '/more',
      icon: 'fa-solid fa-ellipsis',
    },
  ];

  const handleNavigation = (path) => {
    // Check if user has unsaved data in Mobile AddGame
    const hasUnsavedData = typeof window !== 'undefined' && window.mobileAddGameHasUnsavedData;
    const isOnAddGamePage = location.pathname === '/Analysis';
    
    // Check if user has pending comment moves in ViewGame
    const hasUnsavedCommentMoves = typeof window !== 'undefined' && window.mobileViewGameHasCommentMoves;
    const isOnViewGamePage = location.pathname.startsWith('/games/');
    
    if (hasUnsavedData && isOnAddGamePage) {
      // Store the pending navigation and show confirmation
      setPendingNavigation(path);
      setShowNavigationConfirm(true);
      return;
    }
    
    if (hasUnsavedCommentMoves && isOnViewGamePage) {
      // Store the pending navigation and show confirmation
      setPendingNavigation(path);
      setShowNavigationConfirm(true);
      return;
    }
    
    // Execute navigation immediately if no unsaved data
    executeNavigation(path);
  };

  const executeNavigation = (path) => {
    // Clear notification indicator when clicking interactions
    if (path === '/interaction') {
      clearNotificationStatus();
    }
    
    if (path === '/profile' && credentials?.handle) {
      history.push(`/profile/${credentials.handle}`);
    } else if (path === '/more') {
      history.push('/more');
    } else {
      history.push(path);
    }
  };

  // Navigation confirmation modal handlers
  const handleNavigationConfirm = () => {
    setShowNavigationConfirm(false);
    if (pendingNavigation) {
      executeNavigation(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleNavigationCancel = () => {
    setShowNavigationConfirm(false);
    setPendingNavigation(null);
  };

  const isActive = (path) => {
    if (path === '/profile' && (location.pathname.startsWith('/profile/') || location.pathname === '/bookmarks')) {
      return true;
    }
    if (path === '/more' && location.pathname === '/more') {
      return true;
    }
    if (path === '/Analysis' && location.pathname === '/Analysis') {
      return true;
    }
    if (path === '/interaction' && location.pathname === '/interaction') {
      return true;
    }
    return location.pathname === path;
  };

  return (
    <>
      <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: '#1a1a1a',
      borderTop: '1px solid #333',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      height: '45px',
      zIndex: 9999, // Increased from 1000 to prevent z-index conflicts
      paddingBottom: 'env(safe-area-inset-bottom, 0px)', // For iPhone notch
      // CSS stability properties to prevent flickering
      willChange: 'transform',
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden'
    }}>
      {navItems.map((item) => (
        <button
          key={item.path}
          onClick={() => handleNavigation(item.path)}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingBottom: '5px',
            backgroundColor: 'transparent',
            border: 'none',
            color: isActive(item.path) ? 'gold' : '#888',
            cursor: 'pointer',
            transition: 'color 0.2s ease',
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            {/* Golden glow background for create button */}
            {item.path === '/Analysis' && (
              <div 
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'rgba(255, 215, 0, 0.3)',
                  borderRadius: '50%',
                  filter: 'blur(8px)',
                  zIndex: -1
                }}
              />
            )}
            <i 
              className={item.icon} 
              style={{ 
                fontSize: '24px'
              }}
            ></i>
            {/* Show notification dot for interactions */}
            {item.path === '/interaction' && hasUnreadNotifications && (
              <div
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#58b047',
                  borderRadius: '50%',
                  border: '2px solid #1a1a1a',
                  zIndex: 10
                }}
              ></div>
            )}
          </div>
        </button>
      ))}
      </div>
      
      {/* Navigation Confirmation Modal */}
      <NavigationConfirmModal
        show={showNavigationConfirm}
        onContinue={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  credentials: state.user.credentials
});

export default connect(mapStateToProps)(BottomNavigation);